import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Divider, 
  Stack,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Facebook, Linkedin, Instagram } from 'lucide-react';
import { CONTACT_PAGE_CONTENT } from '../../features/ContactPage/constants/contactPageData';
// import MadarLogo from '../../imports/Layer3';
 const COLORS = {
  bg: '#000000',
  border: '#3A3A3A',
  primary: '#E80029',
  text: '#A9A9A9',
  white: '#FFFFFF'
};

const FONT_FAMILY = "'Jost', sans-serif";

const SocialButton = ({ href, icon: Icon }: { href: string; icon: any }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      width: 40,
      height: 40,
      border: `1px solid ${COLORS.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      color: COLORS.text,
      '&:hover': {
        borderColor: COLORS.primary,
        bgcolor: 'rgba(232,0,41,0.1)',
        '& svg': { color: COLORS.primary }
      },
      '& svg': {
        transition: 'color 0.3s ease',
        width: 16,
        height: 16
      }
    }}
  >
    <Icon />
  </Box>
);

 const AnimatedLink = ({ 
  to, 
  href, 
  children, 
  variant = "nav",
  onClick 
}: { 
  to?: string; 
  href?: string; 
  children: React.ReactNode; 
  variant?: "nav" | "footer";
  onClick?: (e: React.MouseEvent) => void;
}) => {
  const commonSx = {
    // ... existing commonSx styles ...
    position: 'relative',
    color: COLORS.text,
    textDecoration: 'none',
    fontSize: variant === "nav" 
      ? { xs: '13px', md: '14px' } 
      : { xs: '12px', md: '13px' },
    fontFamily: FONT_FAMILY,
    transition: 'color 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      color: COLORS.primary,
      '& .underline': {
        width: '100%'
      }
    }
  };

  const Content = (
    <>
      <span style={{ position: 'relative' }}>
        {children}
        <Box
          className="underline"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: 0,
            height: '1px',
            bgcolor: COLORS.primary,
            transition: 'width 0.22s ease'
          }}
        />
      </span>
    </>
  );

  if (to) {
    return <Box component={RouterLink} to={to} onClick={onClick} sx={commonSx}>{Content}</Box>;
  }
  return <Box component="a" href={href} onClick={onClick} sx={commonSx}>{Content}</Box>;
};
export function Footer() {
  const theme = useTheme();
  // Matching Tailwind breakpoints: md: 768px, lg: 1024px
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <Box 
      component="footer" 
      sx={{ 
        bgcolor: COLORS.bg, 
        borderTop: `1px solid rgba(58, 58, 58, 0.5)`, 
        position: 'relative', 
        overflow: 'hidden' 
      }}
    >
      {/* Background Subtle Gradient */}
      <Box 
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at bottom, rgba(232,0,41,0.05) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} 
      />

      {/* Main Footer Content */}
      <Container 
        maxWidth={false} 
        sx={{ 
          maxWidth: '1400px', 
          px: { xs: 3, md: 6, lg: 10 }, // px-6 md:px-12 lg:px-20
          py: { xs: 5, md: 6, lg: 8 },  // py-10 md:py-12 lg:py-16
          position: 'relative',
          zIndex: 10
        }}
      >
        <Grid 
          container 
          spacing={{ xs: 5, md: 6, lg: 8 }} // gap-10 md:gap-12 lg:gap-16
          sx={{ mb: { xs: 5, md: 6, lg: 8 } }}
        >
          {/* Logo & Social */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack direction="column">
                          <Box sx={{ width: { xs: 160, md: 180 }, height: { xs: 32, md: 36 }, mb: { xs: 3, md: 4 } }}>
              <Box
                component="img"
                src="/MADAR_LOGO.png"
                alt="Madar Logo"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
              
              {/* Social Icons */}
              <Stack direction="row" spacing={1.5} sx={{ mt: 'auto' }}>
                <SocialButton href="https://www.facebook.com/share/1AVsQByjz2/" icon={Facebook} />
                <SocialButton href="https://www.linkedin.com/company/madar-technical-consulting-services/" icon={Linkedin} />
                <SocialButton href="https://www.instagram.com/madaraero" icon={Instagram} />
              </Stack>
            </Stack>
          </Grid>

          {/* Navigation */}
         <Grid size={{xs:12,md:6,lg:4}}  sx={{ pl: { lg: 6 } }}>
            <Typography
              variant="h6"
              sx={{
                color: COLORS.white,
                fontSize: { xs: '11px', md: '12px' },
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontWeight: 500,
                mb: { xs: 2, md: 3 },
                fontFamily: FONT_FAMILY
              }}
            >
              NAVIGATION
            </Typography>
            <Stack spacing={{ xs: 1, md: 1.5 }} component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
             {[
  { name: 'Solutions', to: '/solutions' },
  { name: 'Industries', to: '/industries' },
  { name: 'Portfolio', to: '/portfolio' },
  { name: 'About', to: '/about' },
  { name: 'Contact', to: '/contact' }
].map((link) => (
  <Box component="li" key={link.name}>
    <AnimatedLink 
      to={link.to}
      variant="nav"
    >
      {link.name}
    </AnimatedLink>
  </Box>
))}
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: COLORS.white,
                fontSize: { xs: '11px', md: '12px' },
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontWeight: 500,
                mb: { xs: 2, md: 3 },
                fontFamily: FONT_FAMILY
              }}
            >
              CONTACT
            </Typography>
            
            <Stack spacing={{ xs: 1.5, md: 2 }}>
              {[
                { label: 'Address', content: <>Wasfi Al Tal Street<br />P.O Box 2233<br />Irbid 21163, Jordan</> },
                { label: 'Contact', content: <>{CONTACT_PAGE_CONTENT.phone.telLabel}{' '}<AnimatedLink href={CONTACT_PAGE_CONTENT.phone.telLink} variant="nav">{CONTACT_PAGE_CONTENT.phone.telNumber}</AnimatedLink><br />{CONTACT_PAGE_CONTENT.phone.faxLabel} <AnimatedLink href={CONTACT_PAGE_CONTENT.phone.faxLink} variant="nav">{CONTACT_PAGE_CONTENT.phone.faxNumber}</AnimatedLink></> },
                { label: 'Email', content: <AnimatedLink href="mailto:info@imadar.com" variant="nav">info@imadar.com</AnimatedLink>, isLink: true }
              ].map((item, index) => (
                <Box key={index} sx={{ color: COLORS.text, fontSize: { xs: '13px', md: '14px' }, fontFamily: FONT_FAMILY }}>
                  <Typography
                    component="p"
                    sx={{
                      color: COLORS.white,
                      fontSize: { xs: '11px', md: '12px' },
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                      mb: 1,
                      fontFamily: FONT_FAMILY
                    }}
                  >
                    {item.label}
                  </Typography>
                  {item.isLink ? item.content : (
                    <Typography component="p" sx={{ color: 'inherit', fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 1.6 }}>
                      {item.content}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ bgcolor: COLORS.border, mb: { xs: 3, md: 4 } }} />

        {/* Bottom Row */}
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 3 }}
          sx={{ 
            color: COLORS.text, 
            fontSize: { xs: '12px', md: '13px' },
            fontFamily: FONT_FAMILY
          }}
        >
          <Typography sx={{ fontSize: 'inherit', fontFamily: 'inherit', color: 'inherit' }}>
            © 2026 Madar Aero. All rights reserved.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}